import { useEffect, useRef, useCallback } from 'react';
import { useNotifications } from '../../../hooks/utils/useNotifications';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/Icon';
import styles from './VulnAlerts.module.scss';

interface UseAutosaveOptions<T> {
  data: T;
  validate?: () => boolean;
  onSave: (data: T) => void;
  delay?: number;
  isLoading?: boolean;
  enabled?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
}

// Deep comparison function for objects
function deepEqual<T>(obj1: T, obj2: T): boolean {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;

  const keys1 = Object.keys(obj1 as any);
  const keys2 = Object.keys(obj2 as any);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual((obj1 as any)[key], (obj2 as any)[key])) return false;
  }
  return true;
}

export function useAutosave<T>({
  data,
  onSave,
  delay = 2000,
  isLoading = false,
  enabled = true,
  onSuccess,
  onError,
  validate,
}: UseAutosaveOptions<T>) {
  const { success, error } = useNotifications();
  const { t } = useTranslation();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const dataRef = useRef<T>(data);
  const lastSavedDataRef = useRef<T>(data);
  const isFirstRender = useRef(true);
  const isUserInteraction = useRef(false);

  const clearAutosaveTimeout = useCallback((): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const handleSaveSuccess = useCallback((): void => {
    isUserInteraction.current = false;
    onSuccess?.();
  }, [onSuccess]);

  const handleSaveError = useCallback((): void => {
    error({
      title: t('global.saveFailed'),
      message: t('global.saveFailedDescription'),
      autoClose: 5000,
    });
    isUserInteraction.current = false;
    onError?.();
  }, [error, onError]);

  const triggerAutosave = useCallback((): void => {
    if (!enabled) return;
    if (deepEqual(dataRef.current, lastSavedDataRef.current)) {
      // cancel pending autosave when the user has reverted back
      clearAutosaveTimeout();
      return;
    }

    // If the current data is invalid (e.g., no criteria or missing required fields), skip autosave
    if (typeof validate === 'function' && !validate()) {
      // cancel any pending save so we never fire with stale data
      clearAutosaveTimeout();
      isUserInteraction.current = false;
      return;
    }

    clearAutosaveTimeout();

    timeoutRef.current = setTimeout(() => {
      onSave(dataRef.current);
    }, delay);
  }, [enabled, isLoading, delay, onSave, validate, clearAutosaveTimeout]);

  // Watch for data changes to trigger autosave
  useEffect(() => {
    if (isFirstRender.current) {
      // Initialize lastSavedData on first render (to compare against future changes)
      isFirstRender.current = false;
      lastSavedDataRef.current = data;
      dataRef.current = data;
      return;
    }
    if (!deepEqual(data, dataRef.current)) {
      dataRef.current = data;
      // Defer triggering autosave to ensure isUserInteraction flag is updated
      setTimeout(() => {
        triggerAutosave();
      }, 0);
    }
  }, [data, triggerAutosave]);

  // Cleanup any pending save on unmount
  useEffect(() => {
    return () => {
      clearAutosaveTimeout();
    };
  }, [clearAutosaveTimeout]);

  const markAsUserInteraction = useCallback((): void => {
    isUserInteraction.current = true;
  }, []);

  const updateLastSavedData = useCallback((newData: T): void => {
    lastSavedDataRef.current = newData;
    dataRef.current = newData;
  }, []);

  return {
    clearAutosaveTimeout,
    handleSaveSuccess,
    handleSaveError,
    markAsUserInteraction,
    updateLastSavedData,
  };
}
